import type { WeeklyLearningGuideNote } from '../types/domain';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

const CACHE_VERSION = 'v1';

export function weeklyLearningGuideNotesCacheKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): string {
  return `cp.weeklyLearningGuideNotes.${CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
}

function normalizeRows(rows: WeeklyLearningGuideNote[]): WeeklyLearningGuideNote[] {
  return [...rows].sort((a, b) => a.monday_iso.localeCompare(b.monday_iso));
}

export function loadWeeklyLearningGuideNotesCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): { found: boolean; rows: WeeklyLearningGuideNote[] } {
  if (!workspaceId || !gradeId || typeof window === 'undefined') {
    return { found: false, rows: [] };
  }
  try {
    const raw = deviceCacheStorage.getItem(
      weeklyLearningGuideNotesCacheKey(workspaceId, academicYear, termNumber, gradeId)
    );
    if (!raw) return { found: false, rows: [] };
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return { found: false, rows: [] };
    return { found: true, rows: normalizeRows(parsed as WeeklyLearningGuideNote[]) };
  } catch {
    return { found: false, rows: [] };
  }
}

export function saveWeeklyLearningGuideNotesCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: WeeklyLearningGuideNote[],
  force = false
): void {
  if (typeof window === 'undefined') return;
  if (!force && !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    deviceCacheStorage.setItem(
      weeklyLearningGuideNotesCacheKey(workspaceId, academicYear, termNumber, gradeId),
      JSON.stringify(normalizeRows(rows))
    );
  } catch {
    /* ignore quota */
  }
}

export function mergeWeeklyLearningGuideNotesCache(
  existing: WeeklyLearningGuideNote[],
  updates: WeeklyLearningGuideNote[]
): WeeklyLearningGuideNote[] {
  const byMonday = new Map(existing.map((row) => [row.monday_iso, row] as const));
  updates.forEach((row) => {
    byMonday.set(row.monday_iso, row);
  });
  return normalizeRows(Array.from(byMonday.values()));
}
